import { useEffect, useState } from "react";

import { parseNumber } from "common/utils/numbers";
import { toPhone } from "common/utils/formatting";
import Spinner from "common/components/Spinner";

import { usePrimaryPhoneMutation, useSendVerificationCodeMutation } from "client/accounts/hooks";
import VerificationInput from "client/common/components/inputs/VerificationInput";
import TextButton from "client/common/components/buttons/TextButton";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Alert from "client/common/components/content/Alert";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import styles from "../../styles/VerifyPhoneDrawer.module.less";

export type VerifyPhoneDrawerProps = {
  phone: string;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  updateClientPhone?: boolean;
};

const CODE_LENGTH = 6;

const VerifyPhoneDrawer = ({
  phone,
  visible,
  onClose,
  onSuccess,
  updateClientPhone = false,
}: VerifyPhoneDrawerProps) => {
  const [code, setCode] = useState("");
  const { close } = useCurrentDrawer();

  const handleClose = () => {
    close();
    onClose();
  };

  const {
    mutate: updatePhone,
    error: updatePhoneError,
    isLoading: isUpdatingPhone,
    reset: resetPhone,
  } = usePrimaryPhoneMutation({
    onSuccess: () => {
      handleClose();
      onSuccess();
    },
  });

  const {
    mutate: resendVerificationCode,
    isLoading: isSendingCode,
    isSuccess: hasSentCode,
    error: sendingCodeError,
    reset: resetSmsCode,
  } = useSendVerificationCodeMutation();

  const error = sendingCodeError || updatePhoneError;
  const nonFieldErrors = error?.errors?.non_field_errors;
  const errorDisplay =
    nonFieldErrors && nonFieldErrors.length > 0 ? nonFieldErrors[0] : error?.detail;

  useEffect(() => {
    if (visible) {
      resetPhone();
      resetSmsCode();
      setCode("");
    }
  }, [resetPhone, resetSmsCode, visible]);

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="We texted you"
        description={
          <>
            Please enter verification code sent to <strong>{toPhone(phone)}</strong>
          </>
        }
        onClose={close}
      />
      <DrawerLayout.Form
        canSubmit={code.length === CODE_LENGTH}
        onSubmit={() =>
          updatePhone({
            code,
            number: parseNumber(phone, 0),
            set_primary_on_client_only: updateClientPhone,
          })
        }
        isLoading={isUpdatingPhone}
        submitText="Submit"
      >
        {hasSentCode && <Alert variant="success">Code resent to {toPhone(phone)}</Alert>}
        {errorDisplay && <Alert variant="danger">{errorDisplay}</Alert>}
        <div className={styles.input}>
          <VerificationInput value={code} onChange={setCode} size={CODE_LENGTH} />
        </div>
        {isSendingCode ? (
          <div className={styles.sending}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.resend}>
            Didn&apos;t get a text?{" "}
            <TextButton
              type="button"
              disabled={isSendingCode}
              onClick={() => resendVerificationCode({ number: parseNumber(phone, 0) })}
              icon="right"
              hasUnderline={true}
            >
              Resend code
            </TextButton>
          </div>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default VerifyPhoneDrawer;
